import { useCallback, useRef, useState } from 'react';

import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';
import {
  DELETE_CANVASSING_SESSION_PIPELINE_JOB_ERROR_MESSAGE,
  deleteCanvassingSessionPipelineJob,
} from './deleteCanvassingSessionPipelineJobApi';

export type DeleteCanvassingSessionPipelineJobInput = {
  sessionId: string;
  jobId: number;
};

export type UseDeleteCanvassingSessionPipelineJobOptions = {
  fetchImpl?: CanvassingSessionFetchImpl;
  onDeleted: () => void;
};

export type UseDeleteCanvassingSessionPipelineJobResult = {
  deletingJobId: number | null;
  deleteError: string | null;
  deleteJob: (input: DeleteCanvassingSessionPipelineJobInput) => void;
};

export const useDeleteCanvassingSessionPipelineJob = ({
  fetchImpl,
  onDeleted,
}: UseDeleteCanvassingSessionPipelineJobOptions): UseDeleteCanvassingSessionPipelineJobResult => {
  const [deletingJobId, setDeletingJobId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const inFlightJobIdRef = useRef<number | null>(null);

  const deleteJob = useCallback(
    ({ sessionId, jobId }: DeleteCanvassingSessionPipelineJobInput): void => {
      if (inFlightJobIdRef.current !== null) return;

      inFlightJobIdRef.current = jobId;
      setDeletingJobId(jobId);
      setDeleteError(null);

      void (async () => {
        try {
          const deleted = await deleteCanvassingSessionPipelineJob(sessionId, jobId, {
            env: resolveClientEnv(),
            fetchImpl,
          });
          if (!deleted) {
            setDeleteError(DELETE_CANVASSING_SESSION_PIPELINE_JOB_ERROR_MESSAGE);
            return;
          }
          onDeleted();
        } catch {
          setDeleteError(DELETE_CANVASSING_SESSION_PIPELINE_JOB_ERROR_MESSAGE);
        } finally {
          if (inFlightJobIdRef.current === jobId) {
            inFlightJobIdRef.current = null;
            setDeletingJobId(null);
          }
        }
      })();
    },
    [fetchImpl, onDeleted],
  );

  return { deletingJobId, deleteError, deleteJob };
};
